import * as React from 'react';
import injectSheet from 'react-jss';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Icon from 'antd/lib/icon';
import Dropdown from 'antd/lib/dropdown';
import CommentIcon from 'src/components/common/icons/CommentIcon';
import { postComment, fetchAllComments, fetchTaggableUsers } from 'src/api/comments';
import ReactHtmlParser from 'react-html-parser';
import { StylesProps } from 'src/Library/Types';
import { useEffect, useState } from 'react';
import { FetchCommentsRequestBodyType, FetchCommentsResponseType, TaggableUserType } from '../../SE.Types';
import InputWithMentions from 'src/components/common/InputWithMentions';
import Menu from 'antd/lib/menu';
import moment from 'moment';
import { parseMention } from 'src/components/common/comments/utils';
import chatCardStyles from '../SEChatActivity.styles';
import ExpandableSearchInput from '../../SECommon/ExpandableSearchInput';
import Loader from 'src/components/common/Loader';
import { useReduxState } from 'src/hooks/useRedux';
import VisibilitySensor from 'react-visibility-sensor';

const styles = theme => ({
  chatInputDiv: {
    background: 'transparent',
    width: '100%',
    minHeight: '50px',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  noChats: {
    width: '100%',
    display: 'flex',
    height: '250px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.bgWhite,
  },
  chatParentDiv: {
    background: 'white',
    borderRadius: '0 4px 4px 0',
    height: 'min(500px, calc(100vh - 220px))',
    minHeight: 0,
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  chatBlock: {
    marginBottom: theme.spacing.sm,
    width: 'fit-content',
    backgroundColor: '#FCFAF4',
    borderRadius: theme.radius.md,
    padding: theme.spacing.ms,
  },
  filtersParentDiv: {
    color: theme.colors.textSecondary,
    fontSize: theme.fontSize.small,
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: 2.5,
    borderBottom: '1px solid #EDEDED',
    paddingBottom: theme.spacing.ms,
  },
  filtersLeftSide: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  filterLabel: {
    marginRight: theme.spacing.sm,
    cursor: 'pointer',
  },
  filterSelectDiv: {
    width: 200,
    marginRight: theme.spacing.lg,
  },
  redText: {
    color: '#DF1D1D',
    cursor: 'pointer',
  },
  noChatLoaderDiv: {
    width: '100%',
    height: '150px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatCardsParentDiv: {
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  commentButtonStyle: {
    color: theme.colors.bgWhite,
    marginTop: '2px',
    backgroundColor: theme.colors.primary,
    border: theme.colors.primary,
    borderRadius: theme.radius.md,
  },
  fixedBottom: {
    borderTop: `1px solid ${theme.colors.border}`,
    left: 0,
    right: 0,
    bottom: 0,
    background: '#fff',
    padding: theme.spacing.ms,
    flexShrink: 0, 
    position: 'absolute',
    zIndex: 20,
  },
  fixedTop: {
    // Not used anymore
  },
  btnLoadMore: {
    textAlign: 'center',
    marginTop: '5px',
  },
  scrollableArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px',
    height: 0,      
    minHeight: 0, 
    overflowAnchor: 'none',
    paddingBottom: '72px',
  },
  ...chatCardStyles(theme),
});

interface SEChatProps extends StylesProps<ReturnType<typeof styles>> {
  shipmentId: string;
}

const SEChat: React.FC<SEChatProps> = ({
  shipmentId,
  classes,
}) => {

  const [queryString, setQueryString] = useState<string>();
  const [isSearchingUser, setIsSearchingUser] = useState<boolean>(false);
  const [loadingChats, setLoadingChats] = useState(false);
  const [data, setData] = useState<FetchCommentsResponseType>();
  const [chatRawInput, setChatRawInput] = useState<string>('');
  const [isAddingNewChatMessage, setIsAddingNewChatMessage] = useState<boolean>(false);
  const [loadingMoreChats, setLoadingMoreChats] = useState<boolean>(false);
  const [userFilterValue, setUserFilterValue] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<TaggableUserType[]>([]);
  const [sortByFilterValue, setSortByFilterValue] = useState<string | 'updated_at'>('updated_at');
  const [chatInputKey, setChatInputKey] = useState<number>(0);
  let chatRequestBody: FetchCommentsRequestBodyType = {
    sourceObjectId: shipmentId,
    userList: [],
    searchQuery: '',
    currentPageNumber: 1,
    descendingOrder: true,
    lastRowId: '',
    lastSortedColumnValue: '',
    nextOrPrev: 'prev',
    resultPerPage: 10,
    sortBy: "updated_at",
    parentId: null,
  };
  const user = useReduxState(
    ["masterData", "userData"]
  ).value;

  const setTaggableUserData = async () => {
    setIsSearchingUser(true);
    const response: any = await fetchTaggableUsers(
      {
        sourceObjectId: shipmentId,
        sourceObjectType: 'SHIPMENT',
      },
      'execution'
    );
    if (response.isSuccess) {
      setUserOptions(response?.data as TaggableUserType[]);
    }
    setIsSearchingUser(true);
  };

  const setChatData = async (appendNewData = false) => {
    if (!shipmentId) {
      return;
    }
    if (appendNewData) {
      setLoadingMoreChats(true);
    } else {
      setLoadingChats(true);
    }

    const response = await fetchAllComments({
      ...chatRequestBody,
      sourceObjectId: shipmentId,
    }, 'execution');

    if (response.isSuccess) {
      const res = response.response;
      if (appendNewData) {
        setData({
          ...res as FetchCommentsResponseType,
          data: [...res?.data.reverse(), ...(data?.data || [])]
        });
      } else {
        if (res?.data) {
          res.data = res?.data.reverse()
        }
        setData(res as FetchCommentsResponseType);
      }
    }
    setLoadingChats(false);
    setLoadingMoreChats(false);
  }

  useEffect(() => {
    setTaggableUserData();
  }, []);

  useEffect(() => {
    const userList = [...userFilterValue];
    chatRequestBody = {
      ...chatRequestBody,
      searchQuery: queryString || '',
      userList,
    };

    setChatData();
  }, [userFilterValue, sortByFilterValue, queryString]);

  const resetPagination = () => {
    const initialPaginationState: Partial<FetchCommentsRequestBodyType> = {
      nextOrPrev: 'first',
      lastSortedColumnValue: '',
      lastRowId: '',
      currentPageNumber: 1,
      resultPerPage: 10,
    }
    chatRequestBody = {
      ...chatRequestBody,
      ...initialPaginationState,
    };
    setChatData();
  }

  const getTextFromHtml = (html: string): string => {
    let text = '';
    ReactHtmlParser(html, {
      transform: (node, index) => {
        if (node.type === 'text') {
          text = text.concat(node.data);
        }
      },
    });
    return text;
  }

  const addChatMessage = async () => {
    if (!chatRawInput?.length) {
      return;
    }

    setIsAddingNewChatMessage(true);
    const body: {
      sourceObjectId: string;
      parentId: string | null;
      rawHtml: string;
      text: string;
    } = {
      sourceObjectId: shipmentId,
      parentId: null,
      rawHtml: chatRawInput,
      text: getTextFromHtml(chatRawInput),
    };

    const response = await postComment(body, 'execution');
    if (response.isSuccess) {
      setChatInputKey(chatInputKey + 1);
      setChatRawInput('');
      setChatData();
    }
    setIsAddingNewChatMessage(false);
  }

  const showMoreComments = () => {
    if (loadingChats || loadingMoreChats) {
      return;
    }

    chatRequestBody = {
      ...chatRequestBody,
      nextOrPrev: 'next',
      lastRowId: data?.lastItemIdNext || '',
      lastSortedColumnValue: data?.lastSortedColumnValueNext || '',
    };

    setChatData(true);
  }

  const renderShowMore = () => {
    const { isNextPresent } = data || {};

    if (!isNextPresent || loadingMoreChats) {
      return null;
    }

    return (
      <Button onClick={() =>{
        showMoreComments()
      }
      }>
        Load more...
      </Button>
    );
  }

  const renderClearFilters = () => {
    if (!userFilterValue?.length && !queryString?.length &&
      (sortByFilterValue === 'updated_at')
    ) {
      return null;
    }
    return (
      <div
        className={`${classes.filterLabel} ${classes.redText}`}
        onClick={() => {
          setUserFilterValue([]);
          setSortByFilterValue('updated_at');
          setQueryString(undefined);
        }}>
        Reset Filter
      </div>
    );
  }

  const renderChatInput = () => {
    const disableCommentButton = loadingChats || !chatRawInput;

    return (
      <div className={classes.chatInputDiv}>
        <InputWithMentions
          key={chatInputKey}
          onChange={rawText => setChatRawInput(rawText)}
          suggestions={[{
            trigger: '@',
            type: 'user',
            list: userOptions?.map((user) => ({
              id: user.id,
              display: `${user.name} (${user.organisationName})`,
              data: user,
            })),
          }]} />
        <Button
          type="primary"
          className={classes.commentButtonStyle}
          onClick={addChatMessage}
          disabled={disableCommentButton}
        >Comment</Button>
      </div>
    );
  }

  const renderUserFilterDropdown = () => {
    const userOptionsToShow = userOptions?.map(item => ({
      key: item.id,
      label: `${item.name} (${item.organisationName})`,
      details: JSON.stringify(item),
    }));

    return (
      <>
        <div className={classes.filterLabel}>{'Users'}</div>
        <Select
          allowClear
          clearIcon={<Icon type="close-circle" theme="filled" style={{ color: "#DF1D1D" }} />}
          mode='multiple'
          maxTagCount={1}
          maxTagTextLength={6}
          className={classes.filterSelectDiv}
          value={userFilterValue}
          placeholder={'All'}
          optionFilterProp='label'
          onChange={(e) => setUserFilterValue(e)}
        >
          {userOptionsToShow.map(item => <Select.Option key={item.key} label={item.label}>
            {item.label}
          </Select.Option>)}
        </Select>
      </>
    );
  }

  const renderSortBy = () => {
    const sortByOptionsToShow = [
      {
        key: 'updated_at',
        label: 'Newest To Oldest',
      },
      {
        key: 'oldest',
        label: 'Oldest To Newest',
      },
    ];

    const sortByMenu = (
      <Menu onClick={(item) => {
        setSortByFilterValue(item.key as string)
      }} >
        {sortByOptionsToShow.map(item => (
          <Menu.Item key={item.key}>{item.label}</Menu.Item>
        ))}
      </Menu>
    );

    return (
      <Dropdown overlay={sortByMenu} placement="bottomLeft">
        <div className={classes.filterLabel} style={{ marginRight: 24 }}>
          Sort By
        </div>
      </Dropdown>
    );
  }

  const renderSearchBar = () => {
    return (
      <ExpandableSearchInput
        value={queryString}
        onChange={(e) => setQueryString(e.target.value)}
      />
    );
  }

  const renderHeader = () => {
    return (
      <div className={classes.filtersParentDiv}>
        <div className={classes.filtersLeftSide}>
          {renderUserFilterDropdown()}
          {renderSortBy()}
          {renderClearFilters()}
        </div>
        <div>
          {renderSearchBar()}
        </div>
      </div>
    );
  };

  const renderNoComments = () => {
    return (
      <div className={classes.noChats}>
        <div>
          <CommentIcon style={{ width: '40px', height: '40px' }} />
        </div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>
          No comments made so far!
        </div>
      </div>
    );
  }

  const renderChats = () => {
    if (loadingChats) {
      return (
        <div className={classes.noChatLoaderDiv}>
          <Loader zIndex={1} />
        </div>
      );
    }

    const chats = data?.data;

    if (!chats?.length) {
      return renderNoComments();
    }

    return (
      <div className={classes.chatCardsParentDiv}>
        {(chats || []).map(chat => {
          const isCurrentUser = parseInt(user?.id) === chat.userDetails.id;
          return (
            <div key={chat.id} style={{ display: 'flex', flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}>
              <div className={classes.chatBlock} style={{ backgroundColor: isCurrentUser ? '#EAF5FF' : '#FCFAF4' }}>
                <div className={classes.activityBlockHeader} style={{ flexDirection: isCurrentUser ? 'row-reverse' : 'row' }}>
                  <div style={{ marginLeft: isCurrentUser ? 12 : 0 }}>
                    <div className={classes.activityCreatorNameDiv}>{chat.userDetails.name}</div>
                    <div className={classes.activityOrgNameDiv} style={{ textAlign: isCurrentUser ? 'end' : 'start' }}>{chat.organisationName}</div>
                  </div>
                  <div className={classes.activityCreatedAtDiv} style={{ marginLeft: isCurrentUser ? 0 : 12 }}>{moment(chat.updatedAt).format('DD MMM YYYY | hh:mm A')}</div>
                </div>
                <div className={classes.activityContentDiv} style={{ textAlign: isCurrentUser ? 'end' : 'start' }}>
                  {parseMention({
                    html: chat.rawHtml,
                    renderNode: (userId) => {
                      const user = userOptions && userOptions.find(user => user.id === userId);
                      return user ? <span style={{ color: '#1890FF' }}>
                        {user.name}
                      </span> : undefined;
                    },
                  })}
                </div>
              </div>
            </div>
          );
        })}
        {loadingMoreChats && (
          <Loader zIndex={1} />
        )}
      </div>
    );
  }

  return (
    <div className={classes.chatParentDiv}>
      
      <div className={classes.scrollableArea}>
        {renderHeader()}
        <div className={classes.btnLoadMore}>
          {renderShowMore()}
        </div>
        {renderChats()}
      </div>
      
      
      <div className={classes.fixedBottom}>
        {renderChatInput()}
      </div>
    </div>
  );
}
export default injectSheet(styles)(SEChat);
